import Cookies from 'universal-cookie'
import { Provider } from '../types/types'
import { Tokens } from '../types/tokenTypes'

export const names = {
  refreshToken: 'dustin.auth.jwt.refresh_token',
  provider: 'dustin.auth.provider',
  idToken: 'dustin.auth.jwt.id_token',
  accessToken: 'dustin.auth.jwt.microsite_token',
  impersonatedContact: 'dustin.impersonated_contact',
  contactId: 'dustin.contact_id',
  userIdentifier: 'UserIdentifier',
  cookieSession: 'CookieSession',
  sessionUserIdentifier: 'SessionUserIdentifier',
  showMargins: 'showmargins',
  workerErpIdentifier: 'WorkerErpIdentifier',
  punchOutTicket: 'PunchOutTicket',
  zendeskToken: 'dustin.auth.jwt.zendesk_token',
  customerId: 'dustin.customer_id',
}

const get = (cookies: Cookies, name: string) => {
  return cookies.get(name)
}

const set = (
  cookies: Cookies,
  name: string,
  value: string,
  isPunchout = false,
  options: {
    expires?: Date
    secure?: boolean
    sameSite?: boolean | 'lax' | 'none' | 'strict'
  } = {}
) => {
  cookies.set(name, value, {
    ...{
      httpOnly: false,
      sameSite: getSameSite(isPunchout),
      secure: isSecure(),
      path: '/',
    },
    ...options,
  })
}

const clear = (cookies: Cookies, name: string) => {
  cookies.remove(name, { path: '/' })
}

const getSameSite = (isPunchout: boolean) => {
  if (typeof window === 'undefined') {
    return 'lax'
  }

  const protocol = window.location.protocol.toLocaleLowerCase()

  if (protocol === 'http:') {
    return 'lax'
  }

  return isPunchout ? 'none' : 'lax'
}

const isSecure = () => {
  // can not decide on server side
  if (typeof window === 'undefined') {
    return false
  }

  const protocol = window.location.protocol.toLocaleLowerCase()

  return protocol === 'https:'
}

const getDateOneYearFromNow = (): Date => {
  const date = new Date()
  date.setDate(date.getDate() + 365)

  return date
}

export default (cookieHeader?: string) => {
  const cookies = new Cookies(cookieHeader)

  return {
    getProvider: () => get(cookies, names.provider),
    getIdToken: () => get(cookies, names.idToken),
    getRefreshToken: () => get(cookies, names.refreshToken),
    getAccessToken: () => get(cookies, names.accessToken),
    getZendeskToken: () => get(cookies, names.zendeskToken),
    getContactId: (): number => {
      const contactId = get(cookies, names.contactId)
      if (isNaN(contactId) || isNaN(parseInt(contactId))) {
        return 0
      }
      return parseInt(contactId)
    },
    getImpersonatedContact: () => get(cookies, names.impersonatedContact),
    setContactId: (value: number, isPunchout?: boolean) =>
      set(cookies, names.contactId, value.toString(), isPunchout, {
        expires: getDateOneYearFromNow(),
      }),
    setProvider: (value: Provider) =>
      set(cookies, names.provider, value, value === 'dustin_punchout'),
    setIdToken: (value: string, isPunchout?: boolean) =>
      set(cookies, names.idToken, value, isPunchout),
    setRefreshToken: (value: string, isPunchout?: boolean) =>
      set(cookies, names.refreshToken, value, isPunchout),
    setAccessToken: (value: string, isPunchout?: boolean) =>
      set(cookies, names.accessToken, value, isPunchout),
    setZendeskToken: (value: string, isPunchout?: boolean) =>
      set(cookies, names.zendeskToken, value, isPunchout),

    setTokens: (tokens: Tokens, provider: Provider) => {
      set(cookies, names.provider, provider, provider === 'dustin_punchout')
      set(cookies, names.idToken, tokens.idToken, provider === 'dustin_punchout')
      set(cookies, names.refreshToken, tokens.refreshToken, provider === 'dustin_punchout')
      set(cookies, names.accessToken, tokens.micrositeToken, provider === 'dustin_punchout')
    },
    clearTokens: () => {
      clear(cookies, names.provider)
      clear(cookies, names.idToken)
      clear(cookies, names.refreshToken)
      clear(cookies, names.accessToken)
      clear(cookies, names.impersonatedContact)
    },
    clearCookiesOnSignOut: () => {
      clear(cookies, names.userIdentifier)
      clear(cookies, names.cookieSession)
      clear(cookies, names.sessionUserIdentifier)
      clear(cookies, names.showMargins)
      clear(cookies, names.workerErpIdentifier)
      clear(cookies, names.punchOutTicket)
    },
    getUserIdentifier: () => get(cookies, names.userIdentifier),
  }
}
